<script setup lang="ts" generic="T extends { duid: string; title: string; locked?: boolean }">
import { computed, nextTick, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { DotsHorizontalIcon, LockIcon, TrashIcon, VerticalDragHandleIcon } from "~/icons";
import { DialogMode, Placement } from "~/shared/enums";
import type { DropdownMenuSection } from "~/shared/types";
import { when } from "~/utils/wait";

const props = defineProps<{
  item: T;
  isFirst?: boolean;
  deleteDialogTitle: string;
  deleteDialogDescription: string;
  dropdownSections: DropdownMenuSection[];
  lockedTooltip?: string;
  titleExists: (title: string) => boolean;
  onSave: (title: string) => Promise<void>;
  onDelete: () => void;
}>();

const titleInput = ref<HTMLInputElement | null>(null);
const deleteDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const dropdownMenu = ref<ComponentExposed<typeof DropdownMenu> | null>(null);

const title = ref(props.item.title);
const inputId = computed(() => `item-title-${props.item.duid}`);
const hasUserTyped = ref(false);

const isTitleInvalid = computed(() => {
  if (!hasUserTyped.value) {
    return false;
  }
  return title.value.trim() === "" || props.titleExists(title.value);
});

watch(
  () => props.item,
  (newItem) => {
    title.value = newItem.title;
    hasUserTyped.value = false;
  }
);

watch(title, () => {
  hasUserTyped.value = true;
});

const focusTitleInput = () => {
  nextTick(() => {
    titleInput.value?.focus();
    titleInput.value?.select();
  });
};

const startEditing = async () => {
  if (dropdownMenu.value?.isOpen) {
    dropdownMenu.value.close();
    await when(computed(() => !dropdownMenu.value?.isOpen));
  }
  focusTitleInput();
};

const handleSave = async () => {
  const previousTitle = props.item.title;
  const newTitle = title.value.trim();

  // Delete item if name is empty (unless it's locked)
  if (newTitle === "" && !props.item.locked) {
    props.onDelete();
    return;
  }

  if (newTitle === "" && props.item.locked) {
    title.value = previousTitle;
    return;
  }

  if (props.titleExists(newTitle)) {
    if (previousTitle === "" && !props.item.locked) {
      props.onDelete();
      return;
    }
    title.value = previousTitle;
    return;
  }

  if (newTitle === previousTitle) {
    title.value = previousTitle;
    return;
  }

  await props.onSave(newTitle);
  title.value = newTitle;
};

const cancelEditing = () => {
  title.value = props.item.title;
};

const openDeleteDialog = () => {
  deleteDialog.value?.openModal();
};

defineExpose({
  duid: computed(() => props.item.duid),
  startEditing,
  cancelEditing,
  openDeleteDialog,
});
</script>

<template>
  <div class="group/item-row relative flex h-10 w-full items-center">
    <span
      v-if="!item.locked"
      class="dart-handle flex h-10 w-6 shrink-0 cursor-move items-center justify-center opacity-0 group-hover/item-row:opacity-100">
      <VerticalDragHandleIcon class="icon-md text-vlt" />
    </span>
    <span v-else class="flex h-10 w-6 shrink-0" />

    <div
      class="flex h-full flex-1 items-center border-b border-lt group-hover/item-row:bg-lt"
      :class="props.isFirst && 'border-t'">
      <slot name="icon" />

      <div class="flex min-w-0 flex-1 items-center">
        <label :for="inputId" class="sr-only">Title</label>
        <input
          :id="inputId"
          ref="titleInput"
          v-model="title"
          v-auto-width="{ minWidthPx: 40, comfortZone: 1 }"
          type="text"
          placeholder="Title"
          class="min-h-5 w-auto truncate rounded-sm border bg-transparent px-px py-0 text-sm text-md focus-ring-none placeholder:text-vlt"
          :class="{
            'border-danger-base hover:border-danger-hover-light focus:border-danger-base focus:hover:border-danger-base dark:hover:border-danger-hover-dark':
              isTitleInvalid,
            'border-transparent hover:border-hvy focus:border-primary-base focus:hover:border-primary-base':
              !isTitleInvalid,
          }"
          @keydown.enter="handleSave"
          @keydown.esc="cancelEditing"
          @blur="handleSave" />
      </div>

      <div class="flex shrink-0 items-center pr-1">
        <Tooltip v-if="item.locked && lockedTooltip" :text="lockedTooltip">
          <div class="mr-2 flex items-center opacity-0 group-hover/item-row:opacity-100">
            <LockIcon class="icon-sm text-vlt" />
          </div>
        </Tooltip>

        <ConfirmationDialog
          v-if="!item.locked"
          ref="deleteDialog"
          :mode="DialogMode.DELETE"
          :title="deleteDialogTitle"
          :description="deleteDialogDescription"
          confirm-text="Delete"
          cancel-text="Keep"
          :icon="TrashIcon"
          @confirm="onDelete" />

        <DropdownMenu ref="dropdownMenu" :sections="dropdownSections" :placement="Placement.BOTTOM_RIGHT" :distance="1">
          <div
            class="flex size-6 items-center justify-center rounded-sm text-lt opacity-0 group-hover/item-row:opacity-100 hover:bg-md">
            <span class="sr-only">Manage item</span>
            <DotsHorizontalIcon class="icon-sm" />
          </div>
        </DropdownMenu>
      </div>
    </div>
  </div>
</template>
